import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  AdjustmentsHorizontalIcon,
  ArrowDownTrayIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import AdminLayout from "../../layouts/AdminLayout";
import ActionButtons from "../../components/common/ActionButtons";
import DataTable from "../../components/common/DataTable";
import DashboardUserForm from "../../components/users/DashboardUserForm";
import DeleteConfirmationModal from "../../components/common/DeleteConfirmationModal";
import ExpandableCapsules from "../../components/common/ExpandableCapsules";
import Button from "../../components/ui/Button";
import Modal from "../../components/ui/Modal";
import Select from "../../components/ui/Select";
import Spinner from "../../components/ui/Spinner";
import {
  defaultAppSettings,
  defaultRoleRestrictions,
  getAppSettings,
  getDefaultRestrictions,
  saveAppSettings,
} from "../../services/appSettingsService";
import { sortRolesByHierarchy } from "../../utils/roleHierarchy";
import { deleteUser, getUsers } from "../../services/userService";
import { getSocieties } from "../../services/societyService";
import {
  getServices,
  createService,
  updateService,
  deleteService,
} from "../../services/serviceService";
import {
  getAmenities,
  createAmenity,
  updateAmenity,
  deleteAmenity,
} from "../../services/amenityService";
import {
  exportUsers,
  exportSocieties,
  exportBookings,
  exportAuditLogs,
} from "../../services/exportService";
import {
  roleFormSchema,
  zodErrorToFirstMessage,
} from "../../schemas/formSchemas";
import { Globe, Home, Building2, Factory, Landmark, Layers } from "lucide-react";
import BbpsMasterSettingsTab from "./tabs/BbpsMasterSettingsTab";
import styles from "./Settings.module.css";
import Skeleton from "../../components/ui/Skeleton";

const settingsMenus = [
  {
    id: "roles",
    label: "Roles",
    description: "Manage system and custom roles by property level.",
    icon: ShieldCheckIcon,
  },
  {
    id: "scopes",
    label: "Scopes",
    description: "Manage usage scopes and property type links.",
    icon: AdjustmentsHorizontalIcon,
  },
  {
    id: "restrictions",
    label: "Restrictions",
    description: "Manage global portal restrictions and toggles.",
    icon: ShieldCheckIcon,
  },
  {
    id: "admin-users",
    label: "Admin User Management",
    description: "Manage dashboard users and dashboard access separately.",
    icon: UsersIcon,
  },
  {
    id: "bbps-categories",
    label: "BBPS BillPay Categories",
    description: "Master NPCI utility categories & global availability switches.",
    icon: Layers,
  },
  {
    id: "export-data",
    label: "Export Data",
    description: "Prepare reports, exports, and download controls.",
    icon: ArrowDownTrayIcon,
  },
];

const roleTabs = [
  { id: "Residential", label: "Residential" },
  { id: "Commercial", label: "Commercial" },
  { id: "Industrial", label: "Industrial" },
  { id: "System", label: "System" },
];

const adminUserTabs = [
  { id: "dashboard-users", label: "Dashboard User Management" },
  { id: "access-management", label: "Access Management" },
];

const restrictionItems = [
  {
    key: "allowSelfRegistration",
    label: "Allow self registration",
    help: "Residents can create their own accounts without admin help.",
  },
  {
    key: "requireInviteForAdmins",
    label: "Require invite for admin roles",
    help: "Admin and elevated accounts must be invited before access is granted.",
  },
  {
    key: "restrictSocietyCreationToAdmins",
    label: "Restrict society creation to admins",
    help: "Only admin-level roles can add new societies.",
  },
  {
    key: "requireApprovalForResidentAccounts",
    label: "Require approval for resident accounts",
    help: "Owner, tenant, and user roles stay pending until approved.",
  },
  {
    key: "allowRoleEditingForSocietyAdmins",
    label: "Allow society admins to edit roles",
    help: "Society admins can assign or adjust role labels for their teams.",
  },
  {
    key: "enableVisitorAndSecurityRoles",
    label: "Enable visitor and security roles",
    help: "Security and support teams can be assigned operational accounts.",
  },
];

const roleRestrictionItems = [
  {
    key: "canManageUsers",
    label: "Manage users",
    help: "Can create, edit, or disable resident and staff users.",
  },
  {
    key: "canManageAuthentication",
    label: "Manage Authentication",
    help: "Can manage user access, OTP rules, and sessions.",
  },
  {
    key: "canManageVisitors",
    label: "Pre-Registration & Visitor Logs",
    help: "Can access visitor logs, invites, and visitor workflows.",
  },
  {
    key: "canManagePropertyDetails",
    label: "Property Details",
    help: "Can manage society and property definitions.",
  },
  {
    key: "canManageMembers",
    label: "Members",
    help: "Can manage society members, owners, and tenants.",
  },
  {
    key: "canManageDomesticStaff",
    label: "Domestic Staff",
    help: "Can manage domestic staff profiles and approvals.",
  },
  {
    key: "canManageNotices",
    label: "Notices/Announcements",
    help: "Can create and publish notices or announcements.",
  },
  {
    key: "canManageFormsPolicies",
    label: "Forms & Policies",
    help: "Can manage society forms, rules, and policies.",
  },
  {
    key: "canManageServiceBookings",
    label: "Service Booking",
    help: "Can handle amenities and service requests.",
  },
  {
    key: "canManagePayments",
    label: "Payments",
    help: "Can handle dues, accounting actions, and billing workflows.",
  },
  {
    key: "canManageFeedback",
    label: "Feedback & Complaints",
    help: "Can review and resolve feedback and complaints.",
  },
  {
    key: "canManagePolling",
    label: "Polling/Voting",
    help: "Can create and manage polls and voting.",
  },
  {
    key: "canManageVehicleTracking",
    label: "Vehicle Tracking",
    help: "Can manage parking and vehicle assignments.",
  },
  {
    key: "canManageGlobalSettings",
    label: "System Settings",
    help: "Can manage global platform settings, scopes, and roles.",
  },
  {
    key: "canViewSystemLogs",
    label: "System Logs",
    help: "Can view raw system and audit logs.",
  },
  {
    key: "canDelegate",
    label: "Delegate Visitor Approvals",
    help: "Allows delegating incoming visitor approvals to colleagues or floor receptionists.",
  },
];

const placeholderCards = {
  "export-data": {
    title: "Export Data",
    text: "Use this section for resident exports, billing downloads, booking reports, support reports, and audit extracts.",
    items: ["User export", "Society export", "Amenity Booking report", "Audit report"],
  },
};

const createRoleForm = () => ({
  id: null,
  name: "",
  power: 50,
  propertyTypes: ["Residential"],
  scope: "resident",
  is_committee: false,
  assignmentLevel: "",
  description: "",
  restrictions: {
    ...defaultRoleRestrictions,
  },
});

const createScopeForm = () => ({
  id: "",
  name: "",
  description: "",
  propertyTypes: ["Residential"],
});

const normalizeValue = (value = "") => String(value).trim().toLowerCase();

const Settings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(defaultAppSettings);
  const [activeMenu, setActiveMenu] = useState(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const menuParam = params.get("menu");
      if (menuParam && settingsMenus.some((m) => m.id === menuParam)) {
        return menuParam;
      }
    }
    return "roles";
  });
  const [activeRoleTab, setActiveRoleTab] = useState("Residential");
  const [activeAdminTab, setActiveAdminTab] = useState("dashboard-users");
  const [roleForm, setRoleForm] = useState(createRoleForm);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [scopeForm, setScopeForm] = useState(createScopeForm);
  const [editingScopeId, setEditingScopeId] = useState(null);
  const [isScopeModalOpen, setIsScopeModalOpen] = useState(false);
  const [dashboardUsers, setDashboardUsers] = useState([]);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [loadingDashboardUsers, setLoadingDashboardUsers] = useState(false);
  const [societies, setSocieties] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [adminUserModalOpen, setAdminUserModalOpen] = useState(false);
  const [selectedAdminUser, setSelectedAdminUser] = useState(null);
  const [adminUserReadOnly, setAdminUserReadOnly] = useState(false);
  const [error, setError] = useState("");

  const [servicesData, setServicesData] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceFormPayload, setServiceFormPayload] = useState({
    service_name: "",
    society_id: "",
  });

  const [amenitiesData, setAmenitiesData] = useState([]);
  const [loadingAmenities, setLoadingAmenities] = useState(false);
  const [amenityModalOpen, setAmenityModalOpen] = useState(false);
  const [selectedAmenity, setSelectedAmenity] = useState(null);
  const [amenityFormPayload, setAmenityFormPayload] = useState({
    name: "",
    society_id: "",
    description: "",
    rules: "",
    booking_fee: "",
    max_booking_hours: "",
    advance_booking_days: "",
    opening_time: "",
    closing_time: ""
  });



  const managementRoles = useMemo(
    () =>
      sortRolesByHierarchy(
        settings.roles.filter(
          (role) => role.roleType === "system" || role.is_committee
        ),
        "System"
      ),
    [settings.roles],
  );

  const dashboardPermissionCount = useMemo(
    () =>
      managementRoles.reduce(
        (total, role) =>
          total +
          roleRestrictionItems.filter((item) => role.restrictions?.[item.key])
            .length,
        0,
      ),
    [managementRoles],
  );

  const loadSettings = async () => {
    setLoadingSettings(true);
    const data = await getAppSettings();
    setSettings(data);
    setLoadingSettings(false);
  };

  const loadDashboardUsers = useCallback(async () => {
    setLoadingDashboardUsers(true);

    try {
      const allUsers = await getUsers();
      setDashboardUsers(
        allUsers.filter((user) => {
          const normalizedAccountType = normalizeValue(user.account_type);
          const normalizedUserType = normalizeValue(user.system_role || user.primary_role);

          return (
            normalizedAccountType === "management" ||
            managementRoles.some(
              (role) => normalizeValue(role.name) === normalizedUserType,
            )
          );
        }),
      );
    } catch (loadError) {
      console.error(loadError);
      setError("Unable to load dashboard users right now.");
    } finally {
      setLoadingDashboardUsers(false);
    }
  }, [managementRoles]);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const menuParam = params.get("menu");
    if (menuParam && settingsMenus.some((m) => m.id === menuParam)) {
      setActiveMenu((prev) => (prev !== menuParam ? menuParam : prev));
    } else {
      params.set("menu", "roles");
      navigate(`?${params.toString()}`, { replace: true });
    }
  }, [location.search, navigate]);

  const handleMenuChange = (menuId) => {
    setActiveMenu(menuId);
    const params = new URLSearchParams(location.search);
    params.set("menu", menuId);
    navigate(`?${params.toString()}`);
  };

  const loadServicesData = async () => {
    try {
      setLoadingServices(true);
      const data = await getServices();
      setServicesData(data);
    } catch (error) {
      console.error("Failed to load services:", error);
    } finally {
      setLoadingServices(false);
    }
  };

  useEffect(() => {
    if (activeMenu === "services") {
      loadServicesData();
    }
  }, [activeMenu]);

  useEffect(() => {
    if (activeMenu === "admin-users") {
      loadDashboardUsers();
    }
  }, [activeMenu, loadDashboardUsers]);

  useEffect(() => {
    const loadSocieties = async () => {
      try {
        const data = await getSocieties();
        setSocieties(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setSocieties([]);
      }
    };

    loadSocieties();
  }, []);

  const handleRoleRestrictionToggle = (key) => {
    setRoleForm((prev) => ({
      ...prev,
      restrictions: {
        ...prev.restrictions,
        [key]: !prev.restrictions[key],
      },
    }));
  };

  const handleRestrictionToggle = (key) => {
    setSettings((prev) => ({
      ...prev,
      restrictions: {
        ...prev.restrictions,
        [key]: !prev.restrictions[key],
      },
    }));
  };

  const handleSaveRestrictions = async () => {
    const nextSettings = await saveAppSettings(settings);
    setSettings(nextSettings);
    toast.success("Settings saved successfully.");
    setError("");
  };

  const resetRoleForm = () => {
    setRoleForm(createRoleForm());
    setEditingRoleId(null);
    setIsRoleModalOpen(false);
  };

  const openCreateRoleModal = () => {
    const isSystemTab = activeRoleTab === "System";
    setRoleForm({
      ...createRoleForm(),
      propertyTypes: isSystemTab ? [] : [activeRoleTab],
      scope: isSystemTab ? "platform" : "resident",
    });
    setEditingRoleId(null);
    setIsRoleModalOpen(true);
    setError("");
  };

  const handleEditRole = (role) => {
    const isSys = role.roleType === "system" || role.isSystem;
    const rawTypes = role.propertyTypes || (role.propertyType ? [role.propertyType] : []);
    setRoleForm({
      id: role.id,
      name: role.name,
      power: role.power != null ? role.power : 50,
      propertyTypes: isSys ? [] : (Array.isArray(rawTypes) && rawTypes.length > 0 ? rawTypes : ["Residential"]),
      scope: role.scope || (isSys ? "platform" : "resident"),
      is_committee: isSys ? false : (role.is_committee || false),
      assignmentLevel: role.assignmentLevel || "",
      description: role.description || "",
      restrictions: {
        ...defaultRoleRestrictions,
        ...role.restrictions,
      },
    });
    setEditingRoleId(role.id);
    setIsRoleModalOpen(true);
    setError("");
  };

  const handleAddRole = async (e) => {
    e.preventDefault();

    const parsed = roleFormSchema.safeParse(roleForm);
    if (!parsed.success) {
      setError(zodErrorToFirstMessage(parsed.error));
      return;
    }
    const trimmedName = parsed.data.name;
    const trimmedDescription = parsed.data.description;

    const roleExists = settings.roles.some(
      (role) =>
        role.name.toLowerCase() === trimmedName.toLowerCase() &&
        role.id !== editingRoleId,
    );

    if (roleExists) {
      setError("That role already exists.");
      return;
    }

    const existingRole = settings.roles.find((role) => role.id === editingRoleId);
    const isSystemRole = existingRole
      ? (existingRole.isSystem || existingRole.roleType === "system")
      : activeRoleTab === "System";

    if (!isSystemRole && (!roleForm.propertyTypes || roleForm.propertyTypes.length === 0)) {
      setError("Please select at least one property type.");
      return;
    }

    const rolePayload = {
      id: editingRoleId || trimmedName.toLowerCase().replace(/\s+/g, "-"),
      name: trimmedName,
      power: Number(parsed.data.power != null ? parsed.data.power : 50),
      propertyTypes: isSystemRole ? [] : (roleForm.propertyTypes || ["Residential"]),
      scope: roleForm.scope || (isSystemRole ? "platform" : "resident"),
      is_committee: isSystemRole ? false : (roleForm.is_committee || false),
      assignmentLevel: roleForm.assignmentLevel || undefined,
      description: trimmedDescription || (isSystemRole ? "System role" : "Custom role created from settings."),
      isSystem: isSystemRole,
      roleType: isSystemRole ? "system" : "standard",
      restrictions: {
        ...defaultRoleRestrictions,
        ...roleForm.restrictions,
      },
    };

    const nextRoles = editingRoleId
      ? settings.roles.map((role) =>
        role.id === editingRoleId ? { ...role, ...rolePayload } : role,
      )
      : [...settings.roles, rolePayload];

    const nextSettings = await saveAppSettings({
      ...settings,
      roles: nextRoles,
    });

    setSettings(nextSettings);
    resetRoleForm();
    toast.success(
      editingRoleId
        ? "Role updated successfully."
        : "Role created successfully.",
    );
    setError("");
  };

  const resetScopeForm = () => {
    setScopeForm(createScopeForm());
    setEditingScopeId(null);
    setIsScopeModalOpen(false);
  };

  const openCreateScopeModal = () => {
    setScopeForm(createScopeForm());
    setEditingScopeId(null);
    setIsScopeModalOpen(true);
    setError("");
  };

  const handleEditScope = (scope) => {
    setScopeForm({
      id: scope.id,
      name: scope.name,
      description: scope.description || "",
      propertyTypes: scope.propertyTypes || ["Residential"],
    });
    setEditingScopeId(scope.id);
    setIsScopeModalOpen(true);
    setError("");
  };

  const handleSaveScope = async (e) => {
    e.preventDefault();
    const trimmedName = scopeForm.name.trim();
    if (!trimmedName) {
      setError("Scope name is required.");
      return;
    }
    if (!scopeForm.propertyTypes || scopeForm.propertyTypes.length === 0) {
      setError("Select at least one property type.");
      return;
    }

    const normalizedScopeId = (scopeForm.id || trimmedName)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9_-]+/g, "-");

    if (!normalizedScopeId) {
      setError("Valid Scope Slug is required.");
      return;
    }

    const currentScopes = settings.scopes || [];
    const scopeExists = currentScopes.some(
      (s) =>
        (s.name.toLowerCase() === trimmedName.toLowerCase() || s.id === normalizedScopeId) &&
        s.id !== editingScopeId
    );
    if (scopeExists) {
      setError("A scope with that name or slug already exists.");
      return;
    }

    const existingScope = currentScopes.find((s) => s.id === editingScopeId);

    const scopePayload = {
      id: normalizedScopeId,
      name: trimmedName,
      description: scopeForm.description.trim() || "Usage scope for property operations.",
      propertyTypes: scopeForm.propertyTypes,
      isSystem: existingScope ? existingScope.isSystem : false,
    };

    const nextScopes = editingScopeId
      ? currentScopes.map((s) => (s.id === editingScopeId ? scopePayload : s))
      : [...currentScopes, scopePayload];

    // If scope ID changed, update all roles linked to the old scope ID
    let nextRoles = settings.roles;
    if (editingScopeId && editingScopeId !== normalizedScopeId) {
      nextRoles = settings.roles.map((r) =>
        r.scope === editingScopeId ? { ...r, scope: normalizedScopeId } : r
      );
    }

    const nextSettings = await saveAppSettings({
      ...settings,
      scopes: nextScopes,
      roles: nextRoles,
    });

    setSettings(nextSettings);
    resetScopeForm();
    toast.success(
      editingScopeId
        ? "Scope updated successfully."
        : "Scope created successfully."
    );
    setError("");
  };

  const handleDeleteScope = async (scopeId) => {
    const rolesUsingScope = settings.roles.filter((r) => r.scope === scopeId);
    if (rolesUsingScope.length > 0) {
      toast.error(`Cannot delete scope: ${rolesUsingScope.length} role(s) are linked to it.`);
      return;
    }

    const currentScopes = settings.scopes || [];
    const nextScopes = currentScopes.filter((s) => s.id !== scopeId);

    const nextSettings = await saveAppSettings({
      ...settings,
      scopes: nextScopes,
    });

    setSettings(nextSettings);
    toast.success("Scope removed successfully.");
    setError("");
  };

  const handleDeleteRole = async (roleName) => {
    const nextSettings = await saveAppSettings({
      ...settings,
      roles: settings.roles.filter((role) => role.name !== roleName),
    });

    setSettings(nextSettings);
    toast.success(`${roleName} removed successfully.`);
    setError("");
    if (roleForm.name === roleName) {
      resetRoleForm();
    }
  };

  const handleResetRestrictions = () => {
    setSettings((prev) => ({
      ...prev,
      restrictions: getDefaultRestrictions(),
    }));
    toast.success("Restrictions reset to defaults. Save to apply them.");
    setError("");
  };

  const openAdminUserModal = (user = null, readOnly = false) => {
    setSelectedAdminUser(user);
    setAdminUserReadOnly(readOnly);
    setAdminUserModalOpen(true);
    setError("");
  };

  const handleDeleteDashboardUser = async (row) => {
    try {
      await deleteUser(row.user_id);
      await loadDashboardUsers();
      toast.success("Dashboard user deactivated successfully.");
      setError("");
    } catch (deleteError) {
      console.error(deleteError);
      setError("Unable to deactivate this dashboard user right now.");
    }
  };

  const dashboardUserColumns = [
    { header: "Name", accessor: "full_name" },
    { header: "Email ID", accessor: "email_id" },
    { header: "Phone Number", accessor: "mobile_number" },
    { header: "System Role", accessor: "system_role" },
    { header: "Primary Role", accessor: "primary_role" },
    { header: "OS Type", accessor: "os_type" },
    {
      header: "Action",
      render: (row) => (
        <ActionButtons
          row={row}
          onEdit={() => openAdminUserModal(row, false)}
          onDelete={() =>
            setDeleteTarget({
              type: "Dashboard User",
              id: row.user_id,
              name: row.full_name || row.email_id || `User #${row.user_id}`,
              actionLabel: "Deactivate",
              onConfirm: () => handleDeleteDashboardUser(row),
            })
          }
          onView={() => openAdminUserModal(row, true)}
        />
      ),
    },
  ];

  const renderRoleAccess = (filterTab) => {
    const isSystemTab = filterTab === "System";
    const rawFilteredRoles = settings.roles.filter((role) => {
      const isSys = role.roleType === "system" || role.isSystem;
      if (isSystemTab) {
        return isSys;
      }
      if (isSys) {
        return false;
      }
      const pTypes = role.propertyTypes || (role.propertyType ? [role.propertyType] : []);
      return pTypes.includes(filterTab);
    });

    const filteredRoles = sortRolesByHierarchy(rawFilteredRoles, filterTab);

    const customFilteredRoleCount = filteredRoles.filter((role) => !role.isSystem).length;

    return (
      <div className={styles.stack}>
        <section className={[styles.card, styles.cardPad].join(" ")}>
          <div className={styles.roleHeader}>
            <div>
              <h3 className={styles.sectionTitle}>{filterTab} Role Catalog</h3>
              <p className={styles.sectionText}>
                {isSystemTab
                  ? "Platform-wide system roles for administration, IT, finance, and marketing."
                  : `These roles are configured for ${filterTab.toLowerCase()} properties and operations.`}
              </p>
            </div>
            <div className={styles.stats}>
              <div className={styles.statBlue}>
                <div className={styles.statLabel}>Roles</div>
                <div className={styles.statValue}>{filteredRoles.length}</div>
              </div>
              <div className={styles.statAmber}>
                <div className={styles.statLabel}>Custom</div>
                <div className={styles.statValue}>{customFilteredRoleCount}</div>
              </div>
            </div>
            <Button type="button" onClick={openCreateRoleModal}>
              Add Role
            </Button>
          </div>

          <div className={styles.roleGrid}>
            {filteredRoles.map((role) => {
              const enabledRestrictions = roleRestrictionItems.filter(
                (item) => role.restrictions?.[item.key],
              );
              const pTypes = role.propertyTypes || (role.propertyType ? [role.propertyType] : []);
              const linkedScope = (settings.scopes || []).find((s) => s.id === role.scope);

              return (
                <article key={role.id} className={styles.roleCard}>
                  <div className={styles.roleTop}>
                    <div>
                      <h4 className={styles.roleName}>{role.name}</h4>
                      <div className={styles.roleMetaRow}>
                        <p className={styles.roleScope}>
                          Scope: {linkedScope?.name || role.scope || "General"}
                        </p>
                        <span className={styles.powerPill} title="Hierarchy Authority Power">
                          Power: {role.power ?? 50}
                        </span>
                      </div>
                    </div>
                    <span
                      className={[
                        styles.badge,
                        role.isSystem ? styles.badgeSystem : styles.badgeCustom,
                      ].join(" ")}
                    >
                      <span className={styles.badgeDot} />
                      {role.isSystem ? "System" : "Custom"}
                    </span>
                  </div>
                  <p className={styles.roleDesc}>{role.description}</p>

                  {/* Property Types tags */}
                  {role.isSystem || role.roleType === "system" || (pTypes.length === 0 && !role.is_committee) ? (
                    <div className={styles.tags} style={{ marginTop: '8px' }}>
                      <span
                        className={[styles.tag, styles.tagProperty].join(" ")}
                        style={{ background: 'rgba(59, 130, 246, 0.1)', color: 'var(--color-dark-bg)', borderColor: 'rgba(59, 130, 246, 0.25)' }}
                      >
                        Global / Platform
                      </span>
                    </div>
                  ) : (
                    <ExpandableCapsules
                      containerClassName={styles.tags}
                      containerStyle={{ marginTop: '8px' }}
                      variant="property"
                      items={[
                        ...pTypes.map((pt) => ({ type: "property", label: pt })),
                        ...(role.is_committee ? [{ type: "committee", label: "Committee" }] : []),
                      ]}
                      renderItem={(item) =>
                        item.type === "committee" ? (
                          <span
                            key="committee"
                            className={styles.tag}
                            style={{ background: 'rgba(79, 70, 229, 0.12)', color: '#4338ca', borderColor: 'rgba(79, 70, 229, 0.25)' }}
                          >
                            Committee
                          </span>
                        ) : (
                          <span key={item.label} className={[styles.tag, styles.tagProperty].join(" ")}>
                            {item.label}
                          </span>
                        )
                      }
                    />
                  )}

                  {/* Permissions tags */}
                  <ExpandableCapsules
                    containerClassName={styles.tags}
                    containerStyle={{ marginTop: '10px' }}
                    items={enabledRestrictions}
                    emptyLabel="No special restrictions"
                    emptyClassName={[styles.tag, styles.tagEmpty].join(" ")}
                    renderItem={(item) => (
                      <span key={item.key} className={styles.tag}>
                        {item.label}
                      </span>
                    )}
                  />
                  <div className={styles.roleActions}>
                    <button
                      type="button"
                      onClick={() => handleEditRole(role)}
                      className={styles.linkBtn}
                    >
                      <PencilSquareIcon className={styles.iconSm} />
                      Edit Role
                    </button>
                    {!role.isSystem && (
                      <button
                        type="button"
                        onClick={() =>
                          setDeleteTarget({
                            type: "Role",
                            id: role.id || role.name,
                            name: role.name,
                            actionLabel: "Remove",
                            onConfirm: () => handleDeleteRole(role.name),
                          })
                        }
                        className={styles.linkBtn}
                      >
                        Remove Role
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      </div>
    );
  };

  const renderRestrictions = () => (
    <section className={[styles.card, styles.cardPad].join(" ")}>
      <div className={styles.roleHeader}>
        <div>
          <h3 className={styles.sectionTitle}>
            Restrictions
          </h3>
          <p className={styles.sectionText}>
            Control who can sign up, create societies, and edit roles.
          </p>
        </div>
        <button
          type="button"
          onClick={handleResetRestrictions}
          className={styles.linkBtn}
        >
          Reset
        </button>
      </div>

      <div className={styles.stack}>
        {restrictionItems.map((item) => (
          <label
            key={item.key}
            className={styles.checkItem}
          >
            <div>
              <p className={styles.checkTitle}>
                {item.label}
              </p>
              <p className={styles.checkHelp}>
                {item.help}
              </p>
            </div>
            <input
              type="checkbox"
              checked={settings.restrictions[item.key]}
              onChange={() => handleRestrictionToggle(item.key)}
              className={styles.checkbox}
            />
          </label>
        ))}
      </div>

      <div className="mt-5" style={{ marginTop: '20px' }}>
        <Button onClick={handleSaveRestrictions}>Save Restrictions</Button>
      </div>
    </section>
  );

  const renderRoleMenuContent = () => (
    <div className={styles.stack}>
      <div className={[styles.card, styles.cardPad].join(" ")}>
        <div className={styles.tabBar}>
          {roleTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveRoleTab(tab.id)}
              className={[
                styles.tabBtn,
                activeRoleTab === tab.id ? styles.tabBtnActive : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {renderRoleAccess(activeRoleTab)}
    </div>
  );

  const renderScopesMenuContent = () => {
    const currentScopes = settings.scopes || [];

    return (
      <div className={styles.stack}>
        <section className={[styles.card, styles.cardPad].join(" ")}>
          <div className={styles.roleHeader}>
            <div>
              <h3 className={styles.sectionTitle}>Scopes Catalog</h3>
              <p className={styles.sectionText}>
                Scopes categorize roles across usage patterns and link them to Residential, Commercial, or Industrial environments.
              </p>
            </div>
            <div className={styles.stats}>
              <div className={styles.statBlue}>
                <div className={styles.statLabel}>Total Scopes</div>
                <div className={styles.statValue}>{currentScopes.length}</div>
              </div>
              <div className={styles.statAmber}>
                <div className={styles.statLabel}>Custom</div>
                <div className={styles.statValue}>
                  {currentScopes.filter((s) => !s.isSystem).length}
                </div>
              </div>
            </div>
            <Button type="button" onClick={openCreateScopeModal}>
              Add Scope
            </Button>
          </div>

          <div className={styles.roleGrid}>
            {currentScopes.map((scope) => {
              const assignedRoles = settings.roles.filter((r) => r.scope === scope.id);
              const pTypes = scope.propertyTypes || [];

              return (
                <article key={scope.id} className={styles.roleCard}>
                  <div className={styles.roleTop}>
                    <div>
                      <h4 className={styles.roleName}>{scope.name}</h4>
                      <p className={styles.roleScope}>Scope Slug: {scope.id}</p>
                    </div>
                    <span
                      className={[
                        styles.badge,
                        scope.isSystem ? styles.badgeSystem : styles.badgeCustom,
                      ].join(" ")}
                    >
                      <span className={styles.badgeDot} />
                      {scope.isSystem ? "System" : "Custom"}
                    </span>
                  </div>
                  <p className={styles.roleDesc}>{scope.description}</p>

                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--color-muted)', marginBottom: '6px' }}>
                      Linked Property Types
                    </div>
                    <div className={styles.tags}>
                      {pTypes.map((pt) => (
                        <span key={pt} className={[styles.tag, styles.tagProperty].join(" ")}>
                          {pt}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginTop: '12px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', color: 'var(--color-muted)', marginBottom: '6px' }}>
                      Associated Roles ({assignedRoles.length})
                    </div>
                    <div className={styles.tags}>
                      {assignedRoles.length > 0 ? (
                        assignedRoles.map((r) => (
                          <span key={r.id} className={styles.tag}>
                            {r.name}
                          </span>
                        ))
                      ) : (
                        <span className={[styles.tag, styles.tagEmpty].join(" ")}>
                          No roles assigned
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={styles.roleActions}>
                    <button
                      type="button"
                      onClick={() => handleEditScope(scope)}
                      className={styles.linkBtn}
                    >
                      <PencilSquareIcon className={styles.iconSm} />
                      Edit Scope
                    </button>
                    {!scope.isSystem && (
                      <button
                        type="button"
                        onClick={() =>
                          setDeleteTarget({
                            type: "Scope",
                            id: scope.id,
                            name: scope.name,
                            actionLabel: "Remove",
                            onConfirm: () => handleDeleteScope(scope.id),
                          })
                        }
                        className={styles.linkBtn}
                      >
                        Remove Scope
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      </div>
    );
  };


  const renderDashboardUserManagement = () => (
    <div className={styles.stack}>
      <section className={[styles.card, styles.cardPad].join(" ")}>
        <div className={styles.roleHeader}>
          <div>
            <h3 className={styles.sectionTitle}>
              Dashboard User Management
            </h3>
            <p className={styles.sectionText}>
              Create and manage CRM, admin, and dashboard-only users here. App
              residents should stay in the main User Management module.
            </p>
          </div>
          <Button type="button" onClick={() => openAdminUserModal()}>
            Add Dashboard User
          </Button>
        </div>

        <div className={[styles.statsGrid3, styles.sectionBreath].join(" ")}>
          <div className={[styles.statCard, styles.statCardBlue].join(" ")}>
            <div className={styles.statLabel}>Dashboard Users</div>
            <div className={styles.statValue}>{dashboardUsers.length}</div>
          </div>
          <div className={[styles.statCard, styles.statCardIndigo].join(" ")}>
            <div className={styles.statLabel}>Access Roles</div>
            <div className={styles.statValue}>{managementRoles.length}</div>
          </div>
          <div className={[styles.statCard, styles.statCardAmber].join(" ")}>
            <div className={styles.statLabel}>Custom Access Roles</div>
            <div className={styles.statValue}>
              {managementRoles.filter((role) => !role.isSystem).length}
            </div>
          </div>
        </div>

        {loadingDashboardUsers ? (
          <p className={styles.sectionText}>
            <Skeleton variant="list" count={3} />
          </p>
        ) : (
          <div className={styles.tableBreath}>
            <DataTable columns={dashboardUserColumns} data={dashboardUsers} />
          </div>
        )}
      </section>
    </div>
  );

  const renderAccessManagement = () => (
    <div className={styles.stack}>
      <section className={[styles.card, styles.cardPad].join(" ")}>
        <div className={styles.roleHeader}>
          <div>
            <h3 className={styles.sectionTitle}>
              Access Management
            </h3>
            <p className={styles.sectionText}>
              These are the roles that can be used for dashboard and CRM access.
              Resident-facing app users should not be configured here.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setActiveMenu("roles");
              setActiveRoleTab("primary");
            }}
          >
            Open Role Catalog
          </Button>
        </div>

        <div className={[styles.statsGrid3, styles.sectionBreath].join(" ")}>
          <div className={[styles.statCard, styles.statCardBlue].join(" ")}>
            <div className={styles.statLabel}>Dashboard Roles</div>
            <div className={styles.statValue}>{managementRoles.length}</div>
          </div>
          <div className={[styles.statCard, styles.statCardEmerald].join(" ")}>
            <div className={styles.statLabel}>Permission Toggles</div>
            <div className={styles.statValue}>{dashboardPermissionCount}</div>
          </div>
          <div className={[styles.statCard, styles.statCardViolet].join(" ")}>
            <div className={styles.statLabel}>Resident Roles Excluded</div>
            <div className={styles.statValue}>
              {settings.roles.length - managementRoles.length}
            </div>
          </div>
        </div>

        <div className={styles.roleGrid}>
          {managementRoles.map((role) => {
            const enabledRestrictions = roleRestrictionItems.filter(
              (item) => role.restrictions?.[item.key],
            );

            return (
              <article key={role.id} className={styles.roleCard}>
                <div className={styles.roleTop}>
                  <div>
                    <h4 className={styles.roleName}>{role.name}</h4>
                    <div className={styles.roleMetaRow}>
                      <p className={styles.roleScope}>{role.roleType === "system" ? "Platform" : (role.propertyType || "Residential")}</p>
                      <span className={styles.powerPill} title="Hierarchy Authority Power">
                        Power: {role.power ?? 50}
                      </span>
                    </div>
                  </div>
                  <span
                    className={[
                      styles.badge,
                      role.isSystem ? styles.badgeSystem : styles.badgeCustom,
                    ].join(" ")}
                  >
                    <span className={styles.badgeDot} />
                    {role.isSystem ? "System" : "Custom"}
                  </span>
                </div>
                <p className={styles.roleDesc}>{role.description}</p>
                <ExpandableCapsules
                  containerClassName={styles.tags}
                  items={enabledRestrictions}
                  emptyLabel="No dashboard permissions enabled"
                  emptyClassName={[styles.tag, styles.tagEmpty].join(" ")}
                  renderItem={(item) => (
                    <span key={item.key} className={styles.tag}>
                      {item.label}
                    </span>
                  )}
                />
                <div className={styles.roleActions}>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveMenu("roles");
                      handleEditRole(role);
                    }}
                    className={styles.linkBtn}
                  >
                    <PencilSquareIcon className={styles.iconSm} />
                    Edit Access
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );

  const renderAdminUserManagement = () => (
    <div className={styles.stack}>
      <div className={[styles.card, styles.cardPad].join(" ")}>
        <div className={styles.tabBar}>
          {adminUserTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveAdminTab(tab.id)}
              className={[
                styles.tabBtn,
                activeAdminTab === tab.id ? styles.tabBtnActive : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeAdminTab === "dashboard-users" && renderDashboardUserManagement()}
      {activeAdminTab === "access-management" && renderAccessManagement()}
    </div>
  );

  const loadAmenitiesData = useCallback(async () => {
    setLoadingAmenities(true);
    try {
      const data = await getAmenities();
      setAmenitiesData(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError("Unable to load amenities.");
    } finally {
      setLoadingAmenities(false);
    }
  }, []);

  useEffect(() => {
    if (activeMenu === "amenities") {
      loadAmenitiesData();
    }
  }, [activeMenu, loadAmenitiesData]);

  const handleOpenServiceModal = (service = null) => {
    setSelectedService(service);
    if (service) {
      setServiceFormPayload({
        service_name: service.service_name,
        society_id: service.society_id || "",
      });
    } else {
      setServiceFormPayload({ service_name: "", society_id: "" });
    }
    setServiceModalOpen(true);
    setError("");
  };

  const handleDeleteService = async (row) => {
    try {
      await deleteService(row.service_id);
      await loadServicesData();
      toast.success("Service deleted successfully.");
      setError("");
    } catch (err) {
      console.error(err);
      setError("Failed to delete service.");
    }
  };

  const handleSaveService = async (e) => {
    e.preventDefault();
    if (!serviceFormPayload.service_name.trim()) {
      setError("Service name is required.");
      return;
    }
    try {
      if (selectedService) {
        await updateService(selectedService.service_id, serviceFormPayload);
        toast.success("Service updated successfully.");
      } else {
        await createService(serviceFormPayload);
        toast.success("Service created successfully.");
      }
      setServiceModalOpen(false);
      loadServicesData();
      setError("");
    } catch (err) {
      console.error(err);
      setError("Failed to save service.");
    }
  };

  const serviceColumns = [
    { header: "Service Name", accessor: "service_name" },
    {
      header: "Society",
      render: (row) => row.societies?.society_name || "Global",
    },
    {
      header: "Action",
      render: (row) => (
        <ActionButtons
          row={row}
          onEdit={() => handleOpenServiceModal(row)}
          onDelete={() =>
            setDeleteTarget({
              type: "Service",
              id: row.service_id,
              name: row.service_name,
              actionLabel: "Delete",
              onConfirm: () => handleDeleteService(row),
            })
          }
        />
      ),
    },
  ];

  const renderServicesMenuContent = () => (
    <div className={styles.stack}>
      <section className={[styles.card, styles.cardPad].join(" ")}>
        <div className={styles.roleHeader}>
          <div>
            <h3 className={styles.sectionTitle}>
              Services Management
            </h3>
            <p className={styles.sectionText}>
              Manage services available for societies and the Android app.
            </p>
          </div>
          <Button type="button" onClick={() => handleOpenServiceModal()}>
            Add Service
          </Button>
        </div>

        {loadingServices ? (
          <p className={styles.sectionText}>
            <Skeleton variant="list" count={3} />
          </p>
        ) : (
          <div className={styles.tableBreath}>
            <DataTable columns={serviceColumns} data={servicesData} />
          </div>
        )}
      </section>
    </div>
  );

  const handleOpenAmenityModal = (amenity = null) => {
    setSelectedAmenity(amenity);
    if (amenity) {
      setAmenityFormPayload({
        name: amenity.name,
        society_id: amenity.society_id || "",
        description: amenity.description || "",
        rules: amenity.rules || "",
        booking_fee: amenity.booking_fee || "",
        max_booking_hours: amenity.max_booking_hours || "",
        advance_booking_days: amenity.advance_booking_days || "",
        opening_time: amenity.opening_time ? new Date(amenity.opening_time).toISOString().substring(11, 16) : "",
        closing_time: amenity.closing_time ? new Date(amenity.closing_time).toISOString().substring(11, 16) : "",
        type: amenity.type || "booking",
        min_booking_size: amenity.min_booking_size || 1,
        max_booking_size: amenity.max_booking_size || "",
        outsider_fee: amenity.outsider_fee || 0
      });
    } else {
      setAmenityFormPayload({
        name: "",
        society_id: "",
        description: "",
        rules: "",
        booking_fee: "",
        max_booking_hours: "",
        advance_booking_days: "",
        opening_time: "",
        closing_time: "",
        type: "booking",
        min_booking_size: 1,
        max_booking_size: "",
        outsider_fee: 0
      });
    }
    setAmenityModalOpen(true);
    setError("");
  };

  const handleDeleteAmenity = async (row) => {
    try {
      await deleteAmenity(row.amenity_id);
      await loadAmenitiesData();
      toast.success("Amenity deleted successfully.");
      setError("");
    } catch (err) {
      console.error(err);
      setError("Failed to delete amenity.");
    }
  };

  const handleSaveAmenity = async (e) => {
    e.preventDefault();
    if (!amenityFormPayload.name.trim() || !amenityFormPayload.society_id) {
      setError("Amenity name and society are required.");
      return;
    }
    try {
      if (selectedAmenity) {
        await updateAmenity(selectedAmenity.amenity_id, amenityFormPayload);
        toast.success("Amenity updated successfully.");
      } else {
        await createAmenity(amenityFormPayload);
        toast.success("Amenity created successfully.");
      }
      setAmenityModalOpen(false);
      loadAmenitiesData();
      setError("");
    } catch (err) {
      console.error(err);
      setError("Failed to save amenity.");
    }
  };

  const amenityColumns = [
    { header: "Amenity Name", accessor: "name" },
    {
      header: "Society",
      render: (row) => societies.find(s => s.society_id === row.society_id)?.society_name || "Unknown",
    },
    { header: "Fee", accessor: "booking_fee" },
    {
      header: "Action",
      render: (row) => (
        <ActionButtons
          row={row}
          onEdit={() => handleOpenAmenityModal(row)}
          onDelete={() =>
            setDeleteTarget({
              type: "Amenity",
              id: row.amenity_id,
              name: row.name,
              actionLabel: "Delete",
              onConfirm: () => handleDeleteAmenity(row),
            })
          }
        />
      ),
    },
  ];

  const renderAmenitiesMenuContent = () => (
    <div className={styles.stack}>
      <section className={[styles.card, styles.cardPad].join(" ")}>
        <div className={styles.roleHeader}>
          <div>
            <h3 className={styles.sectionTitle}>
              Amenities Management
            </h3>
            <p className={styles.sectionText}>
              Manage amenities available for booking.
            </p>
          </div>
          <Button type="button" onClick={() => handleOpenAmenityModal()}>
            Add Amenity
          </Button>
        </div>

        {loadingAmenities ? (
          <p className={styles.sectionText}>
            <Skeleton variant="list" count={3} />
          </p>
        ) : (
          <div className={styles.tableBreath}>
            <DataTable columns={amenityColumns} data={amenitiesData} />
          </div>
        )}
      </section>
    </div>
  );

  const [exportingStates, setExportingStates] = useState({});

  const handleExport = async (item) => {
    setExportingStates((p) => ({ ...p, [item]: true }));
    try {
      if (item === "User export") await exportUsers();
      else if (item === "Society export") await exportSocieties();
      else if (item === "Booking report") await exportBookings();
      else if (item === "Audit report") await exportAuditLogs();
      else toast.info(`Export for ${item} is not yet implemented.`);
      toast.success(`${item} downloaded successfully!`);
    } catch (err) {
      toast.error(err.message || `Failed to download ${item}`);
    } finally {
      setExportingStates((p) => ({ ...p, [item]: false }));
    }
  };

  const renderPlaceholderMenu = () => {
    const content = placeholderCards[activeMenu];
    if (!content) return null;

    const isExport = activeMenu === "export-data";

    return (
      <section className={[styles.card, styles.cardPad].join(" ")}>
        <h3 className={styles.sectionTitle}>
          {content.title}
        </h3>
        <p className={styles.sectionText}>
          {content.text}
        </p>
        <div className={styles.roleGrid} style={{ marginTop: '20px' }}>
          {content.items.map((item) => (
            <div
              key={item}
              className={styles.statCard}
              style={{ borderStyle: 'dashed', cursor: isExport ? 'pointer' : 'default', opacity: exportingStates[item] ? 0.6 : 1 }}
              onClick={() => isExport && !exportingStates[item] ? handleExport(item) : null}
            >
              {exportingStates[item] ? "Exporting..." : item}
            </div>
          ))}
        </div>
      </section>
    );
  };

  return (
    <AdminLayout>
      <div className={styles.page}>
        <div className={[styles.card, styles.cardPad].join(" ")}>
          <h2 className={styles.headerTitle}>Settings</h2>
          <p className={styles.headerText}>
            Manage roles, dashboard users, images, exports, and future modules
            from one place.
          </p>

          {error && <p className={styles.alertError}>{error}</p>}
        </div>

        <div className={styles.layout}>
          <aside className={[styles.card, styles.cardPad].join(" ")}>
            <div className={styles.sidebarHeader}>
              <h3 className={styles.sidebarTitle}>Settings Menu</h3>
              <p className={styles.sidebarText}>
                Select a menu and manage each area separately.
              </p>
            </div>

            <nav className={styles.menuList}>
              {settingsMenus.map((menu) => {
                const Icon = menu.icon;

                return (
                  <button
                    key={menu.id}
                    type="button"
                    onClick={() => handleMenuChange(menu.id)}
                    className={[
                      styles.menuBtn,
                      activeMenu === menu.id ? styles.menuBtnActive : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <Icon className={styles.menuIcon} />
                    <div>
                      <div className={styles.menuLabel}>{menu.label}</div>
                      <div className={styles.menuDesc}>{menu.description}</div>
                    </div>
                  </button>
                );
              })}
            </nav>
          </aside>

          <div>
            {loadingSettings ? (
              <section className={[styles.card, styles.cardPad].join(" ")} style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <Spinner />
              </section>
            ) : activeMenu === "roles" ? (
              renderRoleMenuContent()
            ) : activeMenu === "scopes" ? (
              renderScopesMenuContent()
            ) : activeMenu === "restrictions" ? (
              renderRestrictions()
            ) : activeMenu === "admin-users" ? (
              renderAdminUserManagement()
            ) : activeMenu === "services" ? (
              renderServicesMenuContent()
            ) : activeMenu === "amenities" ? (
              renderAmenitiesMenuContent()
            ) : activeMenu === "bbps-categories" ? (
              <BbpsMasterSettingsTab />
            ) : (
              renderPlaceholderMenu()
            )}

          </div>
        </div>
      </div>



      <Modal
        isOpen={isRoleModalOpen}
        onClose={resetRoleForm}
        title={
          editingRoleId
            ? (settings.roles.find((r) => r.id === editingRoleId)?.isSystem ? "Edit System Role" : "Edit Role")
            : (activeRoleTab === "System" ? "Create System Role" : "Create New Role")
        }
      >
        {(() => {
          const editingRoleObj = settings.roles.find((r) => r.id === editingRoleId);
          const isSystemModal = editingRoleId
            ? Boolean(editingRoleObj?.isSystem || editingRoleObj?.roleType === "system")
            : activeRoleTab === "System";

          return (
            <form onSubmit={handleAddRole} className={styles.modalForm}>
              {/* ─── Section 1: Role Identity ─── */}
              <div style={{ marginBottom: '20px' }}>
                <h4 className={styles.modalSectionTitle} style={{ marginBottom: '4px' }}>Role Identity</h4>
                <p className={styles.modalSectionText} style={{ marginBottom: '14px' }}>
                  {isSystemModal
                    ? "Define this platform-wide system role and its global operational purpose."
                    : "Give this role a clear name and describe what it does."}
                </p>
                <div className={styles.formGrid}>
                  <div>
                    <label className={styles.label}>Role Name <span style={{ color: '#ef4444' }}>*</span></label>
                    <input
                      type="text"
                      value={roleForm.name}
                      onChange={(e) =>
                        setRoleForm((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      placeholder={isSystemModal ? "e.g. Platform Auditor, Billing Manager" : "e.g. Plant Manager, Receptionist, Owner"}
                      className={styles.textInput}
                    />
                  </div>
                  <div>
                    <label className={styles.label}>Description</label>
                    <input
                      type="text"
                      value={roleForm.description}
                      onChange={(e) =>
                        setRoleForm((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      placeholder="What this role is responsible for"
                      className={styles.textInput}
                    />
                  </div>

                  <div>
                    <label className={styles.label}>Hierarchy Power (1 - 100)</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={roleForm.power ?? 50}
                      onChange={(e) =>
                        setRoleForm((prev) => ({
                          ...prev,
                          power: e.target.value === "" ? "" : Number(e.target.value),
                        }))
                      }
                      placeholder="e.g. 90 (Director), 70 (HOD), 50 (Employee)"
                      className={styles.textInput}
                    />
                    <p style={{ fontSize: '11px', color: 'var(--color-text-muted, #94a3b8)', marginTop: '4px' }}>
                      Determines authority level and catalog order. Higher power roles appear first and hold superior decision/delegation weight.
                    </p>
                  </div>
                </div>
              </div>

              {/* ─── Section 2: Assignment & Scope ─── */}
              <div style={{ marginBottom: '20px', padding: '16px', borderRadius: '12px', background: 'var(--color-surface-secondary, #f8fafc)', border: '1px solid var(--color-border, #e2e8f0)' }}>
                <h4 className={styles.modalSectionTitle} style={{ marginBottom: '4px' }}>Assignment & Scope</h4>
                <p className={styles.modalSectionText} style={{ marginBottom: '14px' }}>
                  {isSystemModal
                    ? "System roles operate platform-wide across all properties and organizations."
                    : "Define where and how this role is applied."}
                </p>

                <div className={styles.formGrid}>
                  <div>
                    <label className={styles.label} style={{ marginBottom: "6px", display: "block" }}>Usage Scope</label>
                    <Select
                      value={roleForm.scope || (isSystemModal ? "platform" : "resident")}
                      onChange={(e) => {
                        const newScope = e.target.value;
                        const scopeDef = (settings.scopes || []).find((s) => s.id === newScope);
                        setRoleForm((prev) => ({
                          ...prev,
                          scope: newScope,
                          propertyTypes: !isSystemModal && scopeDef?.propertyTypes?.length ? scopeDef.propertyTypes : prev.propertyTypes,
                        }));
                      }}
                      options={(settings.scopes || []).map((s) => ({
                        label: s.name,
                        value: s.id,
                      }))}
                    />
                    <p style={{ fontSize: '11px', color: 'var(--color-text-muted, #94a3b8)', marginTop: '4px' }}>
                      {(settings.scopes || []).find(s => s.id === roleForm.scope)?.description || ''}
                    </p>
                  </div>

                  {!isSystemModal && (
                    <div>
                      <label className={styles.label} style={{ marginBottom: "6px", display: "block" }}>Assignment Level</label>
                      <Select
                        value={roleForm.assignmentLevel}
                        onChange={(e) =>
                          setRoleForm((prev) => ({
                            ...prev,
                            assignmentLevel: e.target.value,
                          }))
                        }
                        options={[
                          { label: "Auto (based on role)", value: "" },
                          { label: "Flat / Unit", value: "Flat" },
                          { label: "Tower / Wing / Zone", value: "Tower" },
                          { label: "Entire Property", value: "Property" }
                        ]}
                      />
                      <p style={{ fontSize: '11px', color: 'var(--color-text-muted, #94a3b8)', marginTop: '4px' }}>
                        How broadly this role operates within a property.
                      </p>
                    </div>
                  )}
                </div>

                {isSystemModal ? (
                  <div
                    style={{
                      marginTop: '14px',
                      padding: '12px 14px',
                      borderRadius: '8px',
                      background: 'rgba(59, 130, 246, 0.08)',
                      border: '1px solid rgba(59, 130, 246, 0.25)',
                    }}
                  >
                    <p style={{ fontSize: '13px', fontWeight: 600, color: '#1d4ed8', margin: 0, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <Globe size={15} /> Global Platform Role
                    </p>
                    <p style={{ fontSize: '12px', color: 'var(--color-dark-bg)', margin: '4px 0 0', lineHeight: 1.5 }}>
                      System roles are platform-level and separate from Residential, Commercial, and Industrial property configurations.
                    </p>
                  </div>
                ) : (
                  <>
                    <div style={{ marginTop: '14px' }}>
                      <label className={styles.label}>Applies to Property Types</label>
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '8px' }}>
                        {[
                          { id: "Residential", icon: <Home size={15} />, desc: "Housing societies" },
                          { id: "Commercial", icon: <Building2 size={15} />, desc: "Office buildings" },
                          { id: "Industrial", icon: <Factory size={15} />, desc: "Factories & sites" },
                        ].map((pType) => {
                          const checked = (roleForm.propertyTypes || []).includes(pType.id);
                          return (
                            <label
                              key={pType.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer',
                                padding: '8px 14px',
                                borderRadius: '8px',
                                border: checked ? '2px solid var(--color-primary, #4f46e5)' : '1px solid var(--color-border, #e2e8f0)',
                                background: checked ? 'var(--color-primary-light, rgba(79, 70, 229, 0.08))' : 'transparent',
                                fontSize: '13px',
                                fontWeight: 600,
                                transition: 'all 0.15s ease',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const next = e.target.checked
                                    ? [...(roleForm.propertyTypes || []), pType.id]
                                    : (roleForm.propertyTypes || []).filter((t) => t !== pType.id);
                                  setRoleForm((prev) => ({
                                    ...prev,
                                    propertyTypes: next,
                                  }));
                                }}
                                className={styles.checkbox}
                                style={{ display: 'none' }}
                              />
                              <span style={{ display: 'inline-flex', alignItems: 'center' }}>{pType.icon}</span>
                              <span>{pType.id}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div
                      style={{
                        marginTop: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 14px',
                        borderRadius: '8px',
                        border: roleForm.is_committee ? '2px solid var(--color-primary, #4f46e5)' : '1px solid var(--color-border, #e2e8f0)',
                        background: roleForm.is_committee ? 'var(--color-primary-light, rgba(79, 70, 229, 0.05))' : 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                      onClick={() => setRoleForm((prev) => ({ ...prev, is_committee: !prev.is_committee }))}
                    >
                      <div>
                        <p style={{ fontSize: '13px', fontWeight: 600, color: roleForm.is_committee ? 'var(--color-primary, #4f46e5)' : 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          <Landmark size={15} /> Committee Role
                        </p>
                        <p style={{ fontSize: '11px', color: 'var(--color-text-muted, #94a3b8)', marginTop: '2px' }}>
                          Shows in Society Committee Members on the mobile app
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={roleForm.is_committee || false}
                        onChange={(e) =>
                          setRoleForm((prev) => ({
                            ...prev,
                            is_committee: e.target.checked,
                          }))
                        }
                        className={styles.checkbox}
                        style={{ width: '18px', height: '18px', accentColor: 'var(--color-primary, #4f46e5)' }}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* ─── Section 3: Permissions ─── */}
              <div>
                <h4 className={styles.modalSectionTitle} style={{ marginBottom: '4px' }}>Permissions</h4>
                <p className={styles.modalSectionText} style={{ marginBottom: '12px' }}>
                  What can this role manage? Toggle each area on or off.
                </p>
                <div className={styles.checkGrid}>
                  {roleRestrictionItems.map((item) => (
                    <label key={item.key} className={styles.checkItem}>
                      <div>
                        <p className={styles.checkTitle}>{item.label}</p>
                        <p className={styles.checkHelp}>{item.help}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={roleForm.restrictions[item.key]}
                        onChange={() => handleRoleRestrictionToggle(item.key)}
                        className={styles.checkbox}
                      />
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3" style={{ marginTop: '20px' }}>
                <Button type="submit">
                  {editingRoleId ? "Update Role" : "Create Role"}
                </Button>
                <Button type="button" variant="outline" onClick={resetRoleForm}>
                  Cancel
                </Button>
              </div>
            </form>
          );
        })()}
      </Modal>

      <Modal
        isOpen={isScopeModalOpen}
        onClose={resetScopeForm}
        title={editingScopeId ? "Edit Scope" : "Add Scope"}
      >
        <form onSubmit={handleSaveScope} className={styles.modalForm}>
          <div>
            <label className={styles.label}>Scope Name</label>
            <input
              type="text"
              value={scopeForm.name}
              onChange={(e) => {
                const val = e.target.value;
                setScopeForm((prev) => ({
                  ...prev,
                  name: val,
                  // auto-fill slug if adding a new scope and user hasn't explicitly customized it
                  id: editingScopeId ? prev.id : val.toLowerCase().replace(/[^a-z0-9_-]+/g, "-"),
                }));
              }}
              placeholder="e.g. Operations & Facility"
              className={styles.textInput}
            />
          </div>

          <div>
            <label className={styles.label}>Scope Slug</label>
            <input
              type="text"
              value={scopeForm.id}
              onChange={(e) =>
                setScopeForm((prev) => ({
                  ...prev,
                  id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]+/g, "-"),
                }))
              }
              placeholder="e.g. operations"
              className={styles.textInput}
            />
            <p className={styles.modalSectionText} style={{ marginTop: '4px', fontSize: '11px' }}>
              Used in system logic, API filters, and role assignments (lowercase letters, numbers, hyphens).
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Description
            </label>
            <textarea
              rows="3"
              value={scopeForm.description}
              onChange={(e) =>
                setScopeForm((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Describe who and what this scope encompasses"
              className={styles.textInput}
            />
          </div>

          <div>
            <label className={styles.label}>Linked Property Types</label>
            <p className={styles.modalSectionText} style={{ marginBottom: '8px' }}>
              Select which property environments this scope applies to.
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {["Residential", "Commercial", "Industrial"].map((pType) => {
                const checked = (scopeForm.propertyTypes || []).includes(pType);
                return (
                  <label key={pType} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...(scopeForm.propertyTypes || []), pType]
                          : (scopeForm.propertyTypes || []).filter((t) => t !== pType);
                        setScopeForm((prev) => ({
                          ...prev,
                          propertyTypes: next,
                        }));
                      }}
                      className={styles.checkbox}
                    />
                    {pType}
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex gap-3" style={{ marginTop: '12px' }}>
            <Button type="submit">
              {editingScopeId ? "Update Scope" : "Create Scope"}
            </Button>
            <Button type="button" variant="outline" onClick={resetScopeForm}>
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={adminUserModalOpen}
        onClose={() => setAdminUserModalOpen(false)}
        title={
          adminUserReadOnly
            ? "View Dashboard User"
            : selectedAdminUser
              ? "Edit Dashboard User"
              : "Add Dashboard User"
        }
      >
        <DashboardUserForm
          user={selectedAdminUser}
          readOnly={adminUserReadOnly}
          societies={societies}
          formTitle="Dashboard Access Details"
          onSuccess={() => {
            setAdminUserModalOpen(false);
            loadDashboardUsers();
            toast.success("Dashboard user saved successfully.");
            setError("");
          }}
        />
      </Modal>

      <Modal
        isOpen={serviceModalOpen}
        onClose={() => setServiceModalOpen(false)}
        title={selectedService ? "Edit Service" : "Add Service"}
      >
        <form onSubmit={handleSaveService} className={styles.modalForm}>
          <div className={styles.formGrid}>
            <div>
              <label className={styles.label}>Service Name</label>
              <input
                type="text"
                value={serviceFormPayload.service_name}
                onChange={(e) =>
                  setServiceFormPayload({
                    ...serviceFormPayload,
                    service_name: e.target.value,
                  })
                }
                placeholder="Plumber, Electrician, etc."
                className={styles.textInput}
                required
              />
            </div>
            <div>
              <label className={styles.label} style={{ marginBottom: "6px", display: "block" }}>Society (Optional)</label>
              <Select
                value={serviceFormPayload.society_id}
                onChange={(e) =>
                  setServiceFormPayload({
                    ...serviceFormPayload,
                    society_id: e.target.value,
                  })
                }
                options={[
                  { label: "Global (All Societies)", value: "" },
                  ...societies.map((soc) => ({
                    label: soc.society_name,
                    value: String(soc.society_id),
                  }))
                ]}
              />
            </div>
          </div>
          <div className="mt-5 flex gap-3">
            <Button type="submit">
              {selectedService ? "Update Service" : "Create Service"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setServiceModalOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={amenityModalOpen}
        onClose={() => setAmenityModalOpen(false)}
        title={selectedAmenity ? "Edit Amenity" : "Add Amenity"}
      >
        <form onSubmit={handleSaveAmenity} className={styles.modalForm}>
          <div className={styles.formGrid}>
            <div>
              <label className={styles.label}>Amenity Name</label>
              <input
                type="text"
                value={amenityFormPayload.name}
                onChange={(e) => setAmenityFormPayload({ ...amenityFormPayload, name: e.target.value })}
                placeholder="Pool, Gym, etc."
                className={styles.textInput}
                required
              />
            </div>
            <div>
              <label className={styles.label} style={{ marginBottom: "6px", display: "block" }}>Society</label>
              <Select
                value={amenityFormPayload.society_id}
                onChange={(e) => setAmenityFormPayload({ ...amenityFormPayload, society_id: e.target.value })}
                options={[
                  { label: "Select Society", value: "" },
                  ...societies.map((soc) => ({
                    label: soc.society_name,
                    value: String(soc.society_id),
                  }))
                ]}
              />
            </div>
            <div>
              <label className={styles.label}>Booking Fee</label>
              <input
                type="number"
                value={amenityFormPayload.booking_fee}
                onChange={(e) => setAmenityFormPayload({ ...amenityFormPayload, booking_fee: e.target.value })}
                placeholder="0.00"
                className={styles.textInput}
              />
            </div>
            <div>
              <label className={styles.label}>Max Booking Hours</label>
              <input
                type="number"
                value={amenityFormPayload.max_booking_hours}
                onChange={(e) => setAmenityFormPayload({ ...amenityFormPayload, max_booking_hours: e.target.value })}
                placeholder="2"
                className={styles.textInput}
              />
            </div>
            <div>
              <label className={styles.label}>Advance Booking Days</label>
              <input
                type="number"
                value={amenityFormPayload.advance_booking_days}
                onChange={(e) => setAmenityFormPayload({ ...amenityFormPayload, advance_booking_days: e.target.value })}
                placeholder="30"
                className={styles.textInput}
              />
            </div>
            <div>
              <label className={styles.label}>Opening Time</label>
              <input
                type="time"
                value={amenityFormPayload.opening_time}
                onChange={(e) => setAmenityFormPayload({ ...amenityFormPayload, opening_time: e.target.value })}
                className={styles.textInput}
              />
            </div>
            <div>
              <label className={styles.label}>Closing Time</label>
              <input
                type="time"
                value={amenityFormPayload.closing_time}
                onChange={(e) => setAmenityFormPayload({ ...amenityFormPayload, closing_time: e.target.value })}
                className={styles.textInput}
              />
            </div>
            <div>
              <label className={styles.label}>Is Activity (Badminton, Pool)?</label>
              <div style={{ marginTop: "10px" }}>
                <input
                  type="checkbox"
                  checked={amenityFormPayload.type === "activity"}
                  onChange={(e) => setAmenityFormPayload({ ...amenityFormPayload, type: e.target.checked ? "activity" : "booking" })}
                />
              </div>
            </div>
            <div>
              <label className={styles.label}>Min Booking Size</label>
              <input
                type="number"
                value={amenityFormPayload.min_booking_size}
                onChange={(e) => setAmenityFormPayload({ ...amenityFormPayload, min_booking_size: e.target.value })}
                className={styles.textInput}
                placeholder="1"
              />
            </div>
            <div>
              <label className={styles.label}>Max Booking Size</label>
              <input
                type="number"
                value={amenityFormPayload.max_booking_size}
                onChange={(e) => setAmenityFormPayload({ ...amenityFormPayload, max_booking_size: e.target.value })}
                className={styles.textInput}
                placeholder="No limit"
              />
            </div>
            {amenityFormPayload.type === "activity" && (
              <div>
                <label className={styles.label}>Outsider Fee ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={amenityFormPayload.outsider_fee}
                  onChange={(e) => setAmenityFormPayload({ ...amenityFormPayload, outsider_fee: e.target.value })}
                  className={styles.textInput}
                  placeholder="0.00"
                />
              </div>
            )}
          </div>
          <div className="mt-4">
            <label className={styles.label}>Description</label>
            <textarea
              value={amenityFormPayload.description}
              onChange={(e) => setAmenityFormPayload({ ...amenityFormPayload, description: e.target.value })}
              className={styles.textInput}
              rows="2"
            />
          </div>
          <div className="mt-4">
            <label className={styles.label}>Rules</label>
            <textarea
              value={amenityFormPayload.rules}
              onChange={(e) => setAmenityFormPayload({ ...amenityFormPayload, rules: e.target.value })}
              className={styles.textInput}
              rows="2"
            />
          </div>
          <div className="mt-5 flex gap-3">
            <Button type="submit">
              {selectedAmenity ? "Update Amenity" : "Create Amenity"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAmenityModalOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      <DeleteConfirmationModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        entityType={deleteTarget?.type || "Item"}
        entityId={deleteTarget?.id}
        entityName={deleteTarget?.name || ""}
        canDelete={deleteTarget?.canDelete !== false}
        actionLabel={deleteTarget?.actionLabel || "Delete"}
        onConfirm={deleteTarget?.onConfirm}
      />
    </AdminLayout>
  );
};

export default Settings;
